import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import platformSettingsAPI from '../../services/platformSettingsAPI';
import MaintenanceNoticeModal from './MaintenanceNoticeModal';

export const MAINTENANCE_NOTICE_DISMISSED_KEY = 'sqmt_maintenance_notice_dismissed';

const POLL_MS = 60000;

const readDismissedVersion = () => {
  try {
    return sessionStorage.getItem(MAINTENANCE_NOTICE_DISMISSED_KEY) || '';
  } catch {
    return '';
  }
};

const noticeVersion = (notice) => String(notice?.updated_at || 'enabled');

/**
 * Muestra el aviso de mantención al iniciar sesión y durante la sesión activa (consulta periódica).
 * Cada versión del aviso se muestra una vez por sesión; si el admin lo edita, vuelve a aparecer.
 */
const MaintenanceNoticeGate = () => {
  const { user } = useAuth();
  const [notice, setNotice] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const openVersionRef = useRef('');
  const userId = user?.id;

  const checkNotice = useCallback(async () => {
    let data;
    try {
      data = await platformSettingsAPI.getMaintenanceNotice();
    } catch (error) {
      console.error('Error loading maintenance notice:', error);
      return;
    }
    if (!data?.enabled) {
      openVersionRef.current = '';
      setIsOpen(false);
      return;
    }
    const version = noticeVersion(data);
    if (readDismissedVersion() === version || openVersionRef.current === version) return;
    openVersionRef.current = version;
    setNotice(data);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (!userId) {
      openVersionRef.current = '';
      setNotice(null);
      setIsOpen(false);
      return undefined;
    }

    checkNotice();
    const pollId = setInterval(() => {
      if (document.visibilityState === 'visible') checkNotice();
    }, POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkNotice();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(pollId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [userId, checkNotice]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    openVersionRef.current = '';
    try {
      sessionStorage.setItem(MAINTENANCE_NOTICE_DISMISSED_KEY, noticeVersion(notice));
    } catch {
      /* ignore */
    }
  }, [notice]);

  return <MaintenanceNoticeModal isOpen={isOpen} notice={notice} onClose={handleClose} />;
};

export default MaintenanceNoticeGate;
